import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/config';
import { userPictureUrl } from '@/lib/media';
import ReportModal from './ReportModal';
import { isNativeAndroid, saveArtworkOnDevice } from '@/lib/native';
import ModelCardPlaceholder from './ModelCardPlaceholder';
import '../store-redesign.css';

const ModelViewer = lazy(() => import('./ModelViewer'));

// Visualizador de uma obra em tela cheia. Aqui ficam vídeo/imagem, autor,
// curtidas, coleções, comentários, download e recomendações relacionadas.
const TYPE_LABEL = { image: 'Imagem', video: 'Vídeo', gif: 'GIF', '3d': '3D', game: 'Asset' };

function Lightbox({ post, onClose, authFetch, getMediaUrl, onLikeChange, onOpenProfile, onOpenPost, currentUser, onDeleted, onPostUpdated }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [liked, setLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [related, setRelated] = useState([]);
  const [saved, setSaved] = useState(Boolean(post.is_saved));
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [collections, setCollections] = useState([]);
  const [newCollection, setNewCollection] = useState('');
  const [reportOpen,setReportOpen]=useState(false);
  const [commentReport,setCommentReport]=useState(null);
  const [sensitiveRevealed,setSensitiveRevealed]=useState(false);
  const [relatedRevealed,setRelatedRevealed]=useState(()=>new Set());
  const [coverSaving,setCoverSaving]=useState(false);
  const [saleOpen,setSaleOpen]=useState(false);const [saleBusy,setSaleBusy]=useState(false);
  const [saleEnabled,setSaleEnabled]=useState(Boolean(post.is_for_sale));
  const [salePrice,setSalePrice]=useState(post.price_cents?String((post.price_cents/100).toFixed(2)).replace('.',','):'');
  const [saleLicense,setSaleLicense]=useState(post.license_type||'personal');

  useEffect(() => {
    setLiked(Boolean(post.is_liked));
    setLikesCount(post.likes_count || 0);
    setSaved(Boolean(post.is_saved));
    setCollectionsOpen(false);
    setSensitiveRevealed(false);
    loadComments();
    loadRelated();
    authFetch(`${BACKEND_URL}/api/posts/${post.post_id}/view`,{method:'POST'}).catch(()=>{});
  }, [post.post_id]);

  const loadRelated = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/posts/${post.post_id}/related?limit=18`);
      const data = await response.json();
      setRelated(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
  };

  const loadComments = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/posts/${post.post_id}/comments`);
      const data = await response.json();
      setComments(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleLike = async () => {
    const newLiked = !liked;
    setLiked(newLiked);
    setLikesCount(prev => newLiked ? prev + 1 : prev - 1);
    try {
      await authFetch(`${BACKEND_URL}/api/posts/${post.post_id}/like`, { method: 'POST' });
      onLikeChange && onLikeChange(post.post_id, newLiked);
    } catch (err) {
      setLiked(!newLiked);
      setLikesCount(prev => newLiked ? prev - 1 : prev + 1);
    }
  };

  const loadCollections = async () => {
    const response = await authFetch(`${BACKEND_URL}/api/collections`);
    if (response.ok) setCollections(await response.json());
  };

  const handleSave = async (collectionId = '') => {
    try {
      const suffix = collectionId ? `?collection_id=${encodeURIComponent(collectionId)}` : '';
      const response = await authFetch(`${BACKEND_URL}/api/posts/${post.post_id}/save${suffix}`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Não foi possível salvar');
      setSaved(Boolean(data.saved));
      await loadCollections();
      if (collectionId) setCollectionsOpen(false);
    } catch (err) { console.error(err); }
  };

  const createCollection = async () => {
    const name = newCollection.trim();
    if (!name) return;
    const response = await authFetch(`${BACKEND_URL}/api/collections?name=${encodeURIComponent(name)}`, { method: 'POST' });
    const data = await response.json();
    if (!response.ok) return;
    setNewCollection('');
    await handleSave(data.collection_id);
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submittingComment) return;
    setSubmittingComment(true);
    try {
      const response = await authFetch(
        `${BACKEND_URL}/api/posts/${post.post_id}/comments?content=${encodeURIComponent(newComment)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      setComments(prev => [data, ...prev]);
      setNewComment('');
    } catch (err) {
      console.error(err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const deleteComment = async comment => {
    if(!window.confirm('Excluir este comentário?'))return;
    const response=await authFetch(`${BACKEND_URL}/api/comments/${comment.comment_id}`,{method:'DELETE'});
    const data=await response.json().catch(()=>({}));
    if(!response.ok)return window.dispatchEvent(new CustomEvent('blop:notice',{detail:data.detail||'Não foi possível excluir.'}));
    setComments(old=>old.filter(item=>item.comment_id!==comment.comment_id));
  };

  const handleDownload = async () => {
    try {
      const fileName = `${post.title || 'blopboru-art'}.${post.media_url.split('.').pop()}`;
      if (isNativeAndroid()) {
        await saveArtworkOnDevice(getMediaUrl(post.media_url), fileName);
        window.dispatchEvent(new CustomEvent('blop:notice', { detail: 'Arte salva em Imagens/BlopBoru Art' }));
        return;
      }
      const response = await authFetch(getMediaUrl(post.media_url));
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const saveModelCover=async(blob)=>{
    setCoverSaving(true);
    try{
      const body=new FormData();body.append('thumbnail',blob,'capa-modelo.png');
      const response=await authFetch(`${BACKEND_URL}/api/posts/${post.post_id}/thumbnail`,{method:'PUT',body});
      const data=await response.json().catch(()=>({}));
      if(!response.ok)throw new Error(data.detail||'Não foi possível atualizar a capa.');
      onPostUpdated?.(post.post_id,{thumbnail_url:data.thumbnail_url});
      window.dispatchEvent(new CustomEvent('blop:notice',{detail:'Capa do modelo atualizada.'}));
    }catch(error){window.dispatchEvent(new CustomEvent('blop:notice',{detail:error.message}));}
    finally{setCoverSaving(false);}
  };

  const canDelete = post.user?.user_id === currentUser?.user_id || ['moderator','admin'].includes(currentUser?.role);
  const isOwner=post.user?.user_id===currentUser?.user_id;
  const saveSale=async()=>{setSaleBusy(true);try{const cents=Math.round(Number(salePrice.replace(',','.'))*100);if(saleEnabled&&(!Number.isFinite(cents)||cents<100))throw new Error('Informe um preço a partir de R$ 1,00.');const query=new URLSearchParams({is_for_sale:String(saleEnabled)});if(saleEnabled){query.set('price_cents',String(cents));query.set('license_type',saleLicense);}const response=await authFetch(`${BACKEND_URL}/api/posts/${post.post_id}/sale?${query}`,{method:'PUT'});const data=await response.json().catch(()=>({}));if(!response.ok)throw new Error(data.detail||'Não foi possível atualizar a vitrine.');onPostUpdated?.(post.post_id,data);setSaleOpen(false);window.dispatchEvent(new CustomEvent('blop:notice',{detail:saleEnabled?'Obra atualizada na vitrine.':'Obra retirada da vitrine.'}));}catch(error){window.dispatchEvent(new CustomEvent('blop:notice',{detail:error.message}));}finally{setSaleBusy(false);}};
  const handleDelete = async () => {
    const own = post.user?.user_id === currentUser?.user_id;
    if (!window.confirm(own ? 'Excluir esta publicação definitivamente?' : 'Remover esta publicação como moderador?')) return;
    const reason = own ? 'Excluída pelo autor' : window.prompt('Informe o motivo da remoção:', 'Violação das regras da comunidade');
    if (!reason) return;
    const response = await authFetch(`${BACKEND_URL}/api/posts/${post.post_id}?reason=${encodeURIComponent(reason)}`, {method:'DELETE'});
    const data = await response.json().catch(()=>({}));
    if (!response.ok) return window.dispatchEvent(new CustomEvent('blop:notice',{detail:data.detail||'Não foi possível excluir.'}));
    onDeleted?.(post.post_id);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center art-detail-backdrop"
      style={{ background: 'rgba(5,6,8,0.93)', backdropFilter: 'blur(24px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="lightbox"
    >
      <div 
        className="overflow-hidden w-full h-full flex flex-col animate-fade-up art-detail"
        style={{ background: 'var(--card-bg)', border: '1px solid var(--glass-border)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-5 py-3 sm:py-4 border-b art-detail-header" style={{ borderBottomColor: 'var(--glass-border)' }}>
          <span className="text-base font-semibold" data-testid="lightbox-title">{post.title}</span>
          <button
            onClick={onClose}
            data-testid="lightbox-close"
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm text-white transition-all hover:rotate-90"
            style={{ background: 'var(--glass)', border: '1px solid var(--glass-border)' }}
          >
            <i className="ti ti-x"></i>
          </button>
        </div>

        {/* Content scrollable */}
        <div className="flex-1 overflow-y-auto art-detail-scroll">
          {/* Preview */}
          <div
            className="relative flex items-center justify-center min-h-[220px] sm:min-h-[300px] art-detail-preview"
            style={{ background: '#090b0f' }}
          >
            {post.media_type === '3d' ? (
              <Suspense fallback={<ModelCardPlaceholder title="Carregando modelo 3D…" />}><ModelViewer key={post.post_id} url={getMediaUrl(post.media_url)} authFetch={authFetch} onCaptureCover={post.user?.user_id===currentUser?.user_id?saveModelCover:null} coverSaving={coverSaving} /></Suspense>
            ) : post.media_type === 'video' ? (
              <video
                key={post.post_id}
                src={getMediaUrl(post.media_url)}
                controls
                autoPlay
                loop
                playsInline
                className={`max-w-full max-h-[62vh] sm:max-h-[70vh] object-contain ${post.is_nsfw&&!sensitiveRevealed?'sensitive-media-blur':''}`}
              />
            ) : (
              <img
                src={getMediaUrl(post.media_url)}
                alt={post.title}
                className={`max-w-full max-h-[62vh] sm:max-h-[70vh] object-contain ${post.is_nsfw&&!sensitiveRevealed?'sensitive-media-blur':''}`}
                data-testid="lightbox-image"
              />
            )}
            {post.is_nsfw&&!sensitiveRevealed&&<button type="button" className="sensitive-media-cover sensitive-lightbox-cover" onClick={()=>setSensitiveRevealed(true)}><i className="ti ti-eye-off"/><strong>Conteúdo sensível</strong><span>Esta publicação foi marcada como +18. Toque para visualizar.</span></button>}
            {post.is_for_sale&&!isOwner&&<div className="marketplace-watermark marketplace-watermark-large" aria-hidden="true">BlopBoru</div>}
          </div>

          {/* Meta */}
          <div className="px-5 py-4 flex items-center gap-2.5 flex-wrap border-t art-detail-meta" style={{ borderTopColor: 'var(--glass-border)' }}>
            <span 
              className="rounded-lg px-3 py-1 text-[11px] font-medium"
              style={{
                background: 'rgba(157,123,230,0.15)',
                border: '1px solid rgba(157,123,230,0.3)',
                color: '#bc9eff'
              }}
            >
              {TYPE_LABEL[post.media_type] || 'Media'}
            </span>
            {post.tags?.map(tag => (
              <span 
                key={tag}
                className="rounded-lg px-3 py-1 text-[11px] font-medium"
                style={{
                  background: 'rgba(157,123,230,0.15)',
                  border: '1px solid rgba(157,123,230,0.3)',
                  color: '#bc9eff'
                }}
              >
                #{tag}
              </span>
            ))}
            
            <div className="ml-auto flex items-center gap-3 text-xs text-white/50 art-detail-actions">
              <div className="relative">
                <button onClick={() => { setCollectionsOpen(!collectionsOpen); if (!collectionsOpen) loadCollections(); }}
                  className="flex items-center gap-1.5 hover:text-white transition-colors" aria-label="Salvar em coleção">
                  <i className={saved ? 'ti ti-bookmark-filled text-violet-300' : 'ti ti-bookmark'}></i>
                  <span>{saved ? 'Salvo' : 'Salvar'}</span>
                </button>
                {collectionsOpen && <><button className="collection-mobile-backdrop" aria-label="Fechar coleções" onClick={()=>setCollectionsOpen(false)}></button><div className="collection-popover">
                  <div className="collection-mobile-heading"><b>Salvar em uma coleção</b><button onClick={()=>setCollectionsOpen(false)}><i className="ti ti-x"></i></button></div>
                  <button onClick={() => handleSave()}><i className="ti ti-bookmark"></i> Salvos</button>
                  {collections.filter(c => c.name !== 'Salvos').map(c => <button key={c.collection_id} onClick={() => handleSave(c.collection_id)}>
                    <i className="ti ti-folder"></i>{c.name}<small>{c.items_count}</small>
                  </button>)}
                  <div className="collection-create"><input value={newCollection} onChange={e=>setNewCollection(e.target.value)} maxLength={80} placeholder="Nova coleção" />
                    <button onClick={createCollection} aria-label="Criar coleção"><i className="ti ti-plus"></i></button></div>
                </div></>}
              </div>
              <button
                onClick={handleLike}
                data-testid="lightbox-like-btn"
                className={`flex items-center gap-1.5 hover:text-white transition-colors art-like ${liked?'is-liked':''}`}
              >
                <i className={liked ? 'ti ti-heart-filled text-red-400' : 'ti ti-heart'}></i>
                <span data-testid="lightbox-likes-count">{likesCount}</span>
              </button>
              {post.user?.user_id!==currentUser?.user_id&&<button onClick={()=>setReportOpen(true)} className="flex items-center gap-1.5 hover:text-red-300 transition-colors" aria-label="Denunciar publicação"><i className="ti ti-flag"></i><span>Denunciar</span></button>}
              {canDelete&&<button onClick={handleDelete} className="flex items-center gap-1.5 hover:text-red-300 transition-colors" aria-label="Excluir publicação"><i className="ti ti-trash"></i><span>Excluir</span></button>}
              {isOwner&&<button onClick={()=>setSaleOpen(value=>!value)} className="flex items-center gap-1.5 hover:text-amber-200 transition-colors"><i className="ti ti-shopping-bag"/><span>Vitrine</span></button>}
              {!post.is_for_sale||post.user?.user_id===currentUser?.user_id?<button
                onClick={handleDownload}
                data-testid="lightbox-download-btn"
                className="flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-white text-xs font-semibold transition-all hover:opacity-90 hover:-translate-y-px"
                style={{ background: 'var(--accent)' }}
              >
                <i className="ti ti-download"></i> Baixar Original
              </button>:<button className="marketplace-buy-disabled" title="Pagamentos ainda não foram ativados"><i className="ti ti-shopping-bag"/> R$ {(post.price_cents/100).toLocaleString('pt-BR',{minimumFractionDigits:2})} · Em breve</button>}
            </div>
          </div>
          {saleOpen&&<div className="marketplace-owner-editor"><label><input type="checkbox" checked={saleEnabled} onChange={e=>setSaleEnabled(e.target.checked)}/> Colocar esta obra na vitrine</label>{saleEnabled&&<div className="marketplace-fields"><label>Preço (R$)<input value={salePrice} inputMode="decimal" onChange={e=>setSalePrice(e.target.value.replace(/[^0-9,.]/g,''))}/></label><label>Licença<select value={saleLicense} onChange={e=>setSaleLicense(e.target.value)}><option value="personal">Uso pessoal</option><option value="commercial">Uso comercial</option><option value="exclusive">Exclusiva</option></select></label></div>}<button disabled={saleBusy} onClick={saveSale}>{saleBusy?'Salvando...':'Salvar vitrine'}</button></div>}
          {post.is_for_sale&&<aside className="marketplace-product-card"><div><small>OBRA NA LOJINHA</small><strong>R$ {(post.price_cents/100).toLocaleString('pt-BR',{minimumFractionDigits:2})}</strong></div><div><b>{({personal:'Licença para uso pessoal',commercial:'Licença para uso comercial',exclusive:'Licença exclusiva'})[post.license_type]||'Licença definida pelo artista'}</b><span>{post.license_type==='personal'?'Para uso próprio; não permite revenda nem uso comercial.':post.license_type==='commercial'?'Permite uso em projetos comerciais conforme o contrato da compra.':'Após a compra, a obra deixa de ser oferecida para outras pessoas.'}</span></div><button disabled><i className="ti ti-lock"/> Compra segura em preparação</button></aside>}

          {/* Author + Description */}
          {(post.description || post.user) && (
            <div className="px-5 py-4 border-t space-y-3 art-detail-author" style={{ borderTopColor: 'var(--glass-border)' }}>
              {post.user && (
                <button type="button" onClick={(event) => { event.preventDefault(); event.stopPropagation(); onOpenProfile?.(post.user?.user_id); }} className="flex items-center gap-3 text-left hover:opacity-80 transition-opacity">
                  {post.user.picture ? (
                    <img src={userPictureUrl(post.user.picture, getMediaUrl)} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: 'rgba(255,255,255,0.1)'}}>
                      <i className="ti ti-user text-white/60"></i>
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-semibold">{post.user.name}</div>
                    <div className="text-xs text-white/40">@{post.user.handle}</div>
                  </div>
                </button>
              )}
              {post.description && (
                <p className="text-sm text-white/70 leading-relaxed">{post.description}</p>
              )}
            </div>
          )}

          {/* Comments */}
          <div className="px-5 py-4 border-t" style={{ borderTopColor: 'var(--glass-border)' }}>
            <button type="button" onClick={() => setCommentsOpen(!commentsOpen)} className="w-full flex items-center justify-between text-left">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-white/60">Comentários ({comments.length})</h3>
              <i className={`ti ti-chevron-${commentsOpen ? 'up' : 'down'}`}></i>
            </button>
            {commentsOpen && <div className="pt-4 animate-fade-up">
            <form onSubmit={handleComment} className="flex gap-2 mb-4">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Adicione um comentário..."
                data-testid="comment-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/30"
              />
              <button
                type="submit"
                data-testid="comment-submit"
                disabled={submittingComment || !newComment.trim()}
                className="w-11 h-11 rounded-xl flex items-center justify-center text-white transition-all disabled:opacity-40"
                style={{ background: 'var(--accent)' }}
              >
                <i className="ti ti-send"></i>
              </button>
            </form>

            <div className="space-y-3 max-h-64 overflow-y-auto" data-testid="comments-list">
              {comments.length === 0 ? (
                <p className="text-sm text-white/40 text-center py-4">Seja o primeiro a comentar!</p>
              ) : (
                comments.map(c => (
                  <div key={c.comment_id} className="flex gap-3" data-testid={`comment-${c.comment_id}`}>
                    {c.user?.picture ? (
                      <img src={userPictureUrl(c.user.picture, getMediaUrl)} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{background: 'rgba(255,255,255,0.1)'}}>
                        <i className="ti ti-user text-white/60 text-xs"></i>
                      </div>
                    )}
                    <div className="flex-1 rounded-xl px-3 py-2" style={{background: 'rgba(255,255,255,0.03)'}}>
                      <div className="text-xs font-semibold mb-0.5">{c.user?.name || 'Anônimo'}</div>
                      <div className="text-sm text-white/80">{c.content}</div>
                      <div className="comment-safety-actions">{(c.user?.user_id===currentUser?.user_id||['moderator','admin'].includes(currentUser?.role))&&<button onClick={()=>deleteComment(c)}><i className="ti ti-trash"/> Excluir</button>}{c.user?.user_id!==currentUser?.user_id&&<button onClick={()=>setCommentReport(c)}><i className="ti ti-flag"/> Denunciar</button>}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
            </div>}
          </div>

          {related.length > 0 && (
            <section className="px-5 py-6 border-t art-related" style={{ borderTopColor: 'var(--glass-border)' }}>
              <div className="related-grid">
                {related.map(item => (
                  <button key={item.post_id} type="button" onClick={(event) => { if(item.is_nsfw&&!relatedRevealed.has(item.post_id)){event.preventDefault();setRelatedRevealed(current=>new Set([...current,item.post_id]));return;}onOpenPost?.(item); }} className={`related-card group ${item.is_nsfw&&!relatedRevealed.has(item.post_id)?'related-sensitive':''}`}>
                    {item.media_type === '3d' ? (
                      <ModelCardPlaceholder title={item.title} thumbnail={item.thumbnail_url&&item.thumbnail_url!==item.media_url?getMediaUrl(item.thumbnail_url):''} />
                    ) : item.media_type === 'video' ? (
                      <video src={getMediaUrl(item.media_url)} muted playsInline className="w-full block object-cover" />
                    ) : (
                      <img src={getMediaUrl(item.thumbnail_url || item.media_url)} alt={item.title} className="w-full block object-cover" />
                    )}
                    {item.is_nsfw&&!relatedRevealed.has(item.post_id)&&<span className="related-sensitive-cover"><i className="ti ti-eye-off"/><b>Conteúdo sensível</b><small>Toque para visualizar</small></span>}
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
      {reportOpen&&<ReportModal targetType="post" targetId={post.post_id} targetLabel={post.title} onClose={()=>setReportOpen(false)} authFetch={authFetch}/>} 
      {commentReport&&<ReportModal targetType="post_comment" targetId={commentReport.comment_id} targetLabel={commentReport.content} onClose={()=>setCommentReport(null)} authFetch={authFetch}/>} 
    </div>
  );
}

export default Lightbox;
